import React, { useEffect, useState } from 'react';
import axios from 'axios';

import { 
  Box, Typography, Paper, Button,
  Accordion, AccordionSummary, AccordionDetails,
  Table, TableContainer, TableBody, TableCell, TableHead, TableRow, 
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DashboardNavbar from '../../Components/DashboardNavbar/DashboardNavbar';
import EditAssignmentModal from '../../Components/Modals/EditAssignmentModal';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { useLocation, useNavigate } from 'react-router-dom';

function AssignmentResponses(props) {
    const { state } = useLocation();
    const navigate = useNavigate();

    const [students, setStudents] = useState({});
    const [groups, setGroups] = useState([]);
    const [classSize, setClassSize] = useState(0);
    const [questionStats, setQuestionStats] = useState({});
    const [open, setOpen] = useState(false);

    const questionTypes = {
      "free_response": "Free Response",
      "multiple_choice": "Multiple Choice",
      "likert": "Likert",
      "multiple_select": "Multiple Select",
      "group_survey": "Group Survey",
    }

    function handleClose(updatedAssignment) {
      if (updatedAssignment !== undefined)
        state.assignment = updatedAssignment;
      setOpen(false);
    }
    
    useEffect(() => {
      async function loadClass() {
        const response = await axios.get(`${process.env.REACT_APP_API_HOST}/api/class/getStudents/${state.class_id}`,
          {headers: {Authorization: `Bearer ${state.token.access_token}`}}
        );
        let studentMap = {};
        response.data.forEach((student) => {
          studentMap[student.user_id] = student;
        })
        setStudents(studentMap);
        setClassSize(response.data.length);
      }

      async function getAssignmentStats() {
        let stats = {};
        state.assignment.questions.forEach((question) => {
          let questionStats = {};
          questionStats.totalResponses = question.responses.length;
          questionStats.answers = new Array(question.answers.length).fill(0);
          if (question.type === "likert") {
            questionStats.answers = [0, 0, 0, 0, 0];
            question.answers = ["Strongly Agree", " Agree", "Neutral", "Disagree", "Strongly Disagree"];
            question.responses.forEach((response) => {
              if (response.response[0] === 2) questionStats.answers[0]++;
              else if (response.response[0] === 1) questionStats.answers[1]++;
              else if (response.response[0] === 0) questionStats.answers[2]++;
              else if (response.response[0] === -1) questionStats.answers[3]++;
              else if (response.response[0] === -2) questionStats.answers[4]++;
            })
          }
          else if (question.type === "multiple_choice") {
            question.responses.forEach((response) => {
              questionStats.answers[response.response[0]]++;
            })
          }
          else if (question.type === "multiple_select") {
            question.responses.forEach((response) => {
              response.response.forEach((selectedResponse) => {
                questionStats.answers[selectedResponse]++;
              })
            })
          }
          else if (question.type === "group_survey") {
            questionStats.answers = {};
            question.responses.forEach((response) => {
              questionStats.answers = {
                ...questionStats.answers,
                [response.user_id]: response.response,
              }
            })
          }
          stats[question.question_id] = questionStats;
        });
        setQuestionStats(stats);
      }

      async function loadGroups() {
        const response = await axios.get(`${process.env.REACT_APP_API_HOST}/api/groups/class/${state.class_id}`,
          {headers: {Authorization: `Bearer ${state.token.access_token}`}}
        );
        setGroups(response.data);
      }
      loadClass();
      loadGroups();
      getAssignmentStats();
    }, []);

    return (
        <Box>
            <DashboardNavbar userType="admin"/>
            <div style={{display: "flex", alignItems: "center", gap: 2, paddingTop: 16, paddingLeft: 16}}>
              <Button variant='outlined' sx={{height: 32, alignSelf: "center"}} onClick={() => navigate("/assignments", { state: state })} startIcon={<ArrowBackIcon/>}>
                    Assignments
              </Button>
              <Typography variant="h4" sx={{mx: 2}}>{state.assignment.name}</Typography>
            </div>
            <div style={{display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", marginInline: "auto", maxWidth: "1200px", paddingInline: "32px"}}>
              <div style={{display: "flex", gap: 10}}>
                <Typography>{"Submissions: " + (state.assignment.questions.length > 0 ? state.assignment.questions[0].responses.length : 0)}</Typography>
                <Typography>{"Incomplete submissions: " + (classSize - (state.assignment.questions.length > 0 ? state.assignment.questions[0].responses.length : 0))}</Typography>
              </div>
              <Button variant='contained' onClick={() => setOpen(true)}>Edit Assignment</Button>
            </div>
            <div style={{maxWidth: "1200px", padding: "16px", margin: "auto"}}>
            {
              state.assignment.questions.map((question) => (
                <Accordion key={question.question_id}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="question-header"
                    id={question.question_id}
                  >
                    <div>
                      <Typography sx={{fontSize: "14px"}}>Type: {questionTypes[question.type]}</Typography>
                      <Typography sx={{fontSize: "18px"}}>{question.text}</Typography>
                    </div>
                  </AccordionSummary>
                  <AccordionDetails sx={{maxHeight: 600, overflowY: "auto"}}>
                  {
                    question.type==="free_response" ? 
                    <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                      <Table sx={{ minWidth: 650 }} aria-label="simple table">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{fontWeight: "bold"}}>Student</TableCell>
                            <TableCell align="left" sx={{fontWeight: "bold"}}>Response</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody sx={{maxHeight: 400, overflow: "auto"}}>
                          {question.responses.map((response) => (
                            <TableRow
                              key={response.user_id}
                              sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                            >
                              <TableCell component="th" scope="row">{students[response.user_id]?.first_name + " " + students[response.user_id]?.last_name}</TableCell>
                              <TableCell align="left">{response.response}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    : question.type==="likert" || 
                    question.type==="multiple_choice" || 
                    question.type==="multiple_select" ? 
                    <TableContainer component={Paper}>
                      <Table sx={{ minWidth: 650 }} aria-label="simple table">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{fontWeight: "bold"}}>Response</TableCell>
                            <TableCell align="right" sx={{fontWeight: "bold"}}># of responses</TableCell>
                            <TableCell align="right" sx={{fontWeight: "bold"}}>% of submissions</TableCell>
                            <TableCell align="right" sx={{fontWeight: "bold"}}>% of class</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {question.answers.map((row, index) => (
                            <TableRow
                              key={row+index}
                              sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                            >
                              <TableCell component="th" scope="row">{row}</TableCell>
                              <TableCell align="right">{questionStats[question.question_id]?.answers[index]}</TableCell>
                              <TableCell align="right">{questionStats[question.question_id]?.totalResponses === 0 ? 0 : (questionStats[question.question_id]?.answers[index] / questionStats[question.question_id]?.totalResponses).toFixed(2) * 100 + "%"}</TableCell>
                              <TableCell align="right">{(questionStats[question.question_id]?.answers[index] / classSize).toFixed(2) * 100 + "%"}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    : question.type==="group_survey" ?
                    groups.map((group) => (
                      <Accordion key={group.group_id}>
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          aria-controls="question-header"
                          id={group.group_id}
                        >
                          <Typography sx={{fontSize: "16px"}}>{group.project.name}</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <TableContainer component={Paper}>
                            <Table sx={{ minWidth: 650 }} aria-label="simple table">
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{fontWeight: "bold"}}>Student</TableCell>
                                  {group.users.map((user, index) => (
                                    <TableCell key={index} align="right" sx={{fontWeight: "bold"}}>{user.first_name + " " + user.last_name}</TableCell>
                                  ))}
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {group.users.map((user) => (
                                  <TableRow
                                    key={user.user_id}
                                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                  >
                                    <TableCell component="th" scope="row">{user.first_name + " " + user.last_name}</TableCell>
                                    {group.users.map((member, index) => (
                                      <TableCell key={index} align="right">
                                        {questionStats[question.question_id]?.answers[user.user_id] ? 
                                        questionStats[question.question_id]?.answers[user.user_id][member.user_id] 
                                        : "N/A"}
                                      </TableCell>
                                    ))}
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </AccordionDetails>
                      </Accordion>
                    ))
                    : null
                  }
                  </AccordionDetails>
                </Accordion>
              ))
            }
            </div>
            <EditAssignmentModal open={open} handleClose={handleClose} state={state}/>
        </Box>
    );
}

export default AssignmentResponses;